import { appModel } from "@/app.model";
import { routerModel } from "@/router/router.model";
import { appLanguageSwitcherModel } from "@/shared/components/app-language-switcher/app-language-switcher.model";
import _axios, { AxiosError, AxiosResponse } from "axios";
import { computed } from "vue";
import { eff } from "./effect-ts-abbr";
import { sessionModel } from "@/shared/state/session.model";
import { getAuthRedirectService } from "../services/auth-redirects.service";

const paramsSerializer = (query: Record<string, unknown>) => {
    const params = eff.fn.pipe(
        query,
        eff.dict.filter(
            (value) =>
                value !== Infinity &&
                value !== -Infinity &&
                !(typeof value === "number" && isNaN(value)) &&
                value !== undefined &&
                value !== null &&
                !(Array.isArray(value) && !value.length),
        ),
        eff.dict.map((value) => (Array.isArray(value) ? value.join(",") : value)),
    );

    const searchParams = new URLSearchParams();
    for (const key of Object.keys(params)) {
        const param = params[key];
        if (Array.isArray(param)) {
            for (const p of param) {
                searchParams.append(key, p);
            }
        } else {
            searchParams.append(key, param as string);
        }
    }

    return searchParams.toString();
};

export const axiosWithoutErrorHandling = computed(() => {
    const headers: Record<string, string> = {
        "Accept-Language": appLanguageSwitcherModel.lang.value,
    };

    if (sessionModel.sessionDto.value) {
        headers.Authorization = `Bearer ${sessionModel.sessionDto.value?.token}`;
    }

    const instance = _axios.create({
        headers,
        validateStatus: (status) => status < 400 || status === 501,
        paramsSerializer,
    });

    return instance;
});

export const appAxios = computed(() => {
    const headers: Record<string, string> = {
        "Accept-Language": appLanguageSwitcherModel.lang.value,
    };

    const sessionDto = sessionModel.sessionDto.value;

    const instance = _axios.create({
        headers,
        validateStatus: (status) => status < 400 || status === 501,
        paramsSerializer,
    });

    instance.interceptors.request.use((config) => {
        if (
            sessionDto &&
            !config.url?.startsWith("/client/web/public/") &&
            "Authorization" in config.headers === false
        ) {
            config.headers.Authorization = `Bearer ${sessionDto.token}`;
        }

        return config;
    });

    instance.interceptors.response.use(
        (response: AxiosResponse) => {
            const skipErrorHandler =
                response?.config?.headers["X-Skip-Error-Handler"] === "true" ||
                (response.data?.status?.code === 404 &&
                    response?.config?.url?.includes("/warranty"));
            if (!appModel.initialized.value) {
                return response;
            }

            const statusCode = response.data?.status?.code;

            if (
                (statusCode >= 300 || statusCode < 200) &&
                response.data?.status?.message &&
                (statusCode !== 401 ||
                    (response.config?.url &&
                        !response.config.url.includes("/company/financial") &&
                        !response.config.url.includes("/user/shopping-cart"))) &&
                (response.data?.status?.code !== 403 ||
                    response.config.headers["x-request-id"] !== "mk-session")
            ) {
                if (!skipErrorHandler) {
                    appModel.toast.value.add({
                        severity: "error",
                        summary:
                            response.data.status.innerMessage ||
                            response.data.status.message,
                        life: 3000,
                    });
                }
            } else if (response.data?.error) {
                if (!skipErrorHandler) {
                    appModel.toast.value.add({
                        severity: "error",
                        summary: response.data?.error,
                        life: 3000,
                    });
                }
            }
            return response;
        },
        async (error: AxiosError) => {
            if (!appModel.initialized.value) {
                return Promise.reject(error);
            }

            const statusCode = error.response?.data?.status?.code;

            if (error.response?.status === 401) {
                const url = error.response?.config?.url;
                if (url && !url.startsWith("/client/web/company/legal-docs")) {
                    sessionModel.sessionDto.value = undefined;
                    const authRedirectService = getAuthRedirectService();
                    authRedirectService.afterLoginUrl = window.location.pathname;

                    await routerModel.router?.push(`/login`);

                    if (
                        url &&
                        !(
                            url.includes("/company/financial") ||
                            url.includes("/user/shopping-cart")
                        )
                    ) {
                        appModel.toast.value.add({
                            severity: "error",
                            summary: appModel.i18n.value.t("common.sessionExpired"),
                            life: 3000,
                        });
                    }
                } else {
                    return Promise.reject(error);
                }
            } else if (
                error.response?.data?.status?.message &&
                (statusCode >= 300 || statusCode < 200) &&
                !(
                    statusCode === 401 &&
                    error.response?.config?.url &&
                    (error.response.config.url.includes("/company/financial") ||
                        error.response.config.url.includes("/user/shopping-cart"))
                )
            ) {
                appModel.toast.value.add({
                    severity: "error",
                    summary:
                        error.response.data.status.innerMessage ||
                        error.response.data.status.message,
                    life: 3000,
                });
            } else if (error.response?.data?.error) {
                appModel.toast.value.add({
                    severity: "error",
                    summary: error.response?.data?.error,
                    life: 3000,
                });
            } else if (
                !error.response ||
                (!!error.response?.status &&
                    error.response.status !== 501 &&
                    error.response.status !== 404 &&
                    !error.response.config?.url?.startsWith(
                        "/client/web/company/legal-docs/",
                    ))
            ) {
                appModel.toast.value.add({
                    severity: "error",
                    summary:
                        error.response?.statusText ||
                        appModel.i18n.value.t("common.somethingWentWrong"),
                    life: 3000,
                });
            }

            return Promise.reject(error);
        },
    );

    return instance;
});
